import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, CreditCard, CheckCircle, Eye, EyeOff, Check, X, Loader2, ChevronDown, Gift, Sparkles } from 'lucide-react';
import { handleMobileInput, handleGSTInput, validateMobile, validateGST } from '@/utils/inputValidation';
import ReactCountryFlag from 'react-country-flag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const FEATURE_CARDS = [
  { icon: '💰', title: '60% Avg Margins', color: '#f59e0b' },
  { icon: '⚡', title: 'Instant Activation', color: '#06b6d4' },
  { icon: '🏷️', title: 'Set Your Own Markup', color: '#8b5cf6' },
  { icon: '🌐', title: 'Custom Subdomain', color: '#10b981' },
  { icon: '🤝', title: 'Multi-level Network',color: '#3b82f6' },
  { icon: '📊', title: 'Full Dashboard', color: '#ef4444' },
];




// India first, then rest alphabetically
const COUNTRY_CODES = [
  { code: '+91',   iso: 'IN', country: 'India' },
  { code: '+93',   iso: 'AF', country: 'Afghanistan' },
  { code: '+355',  iso: 'AL', country: 'Albania' },
  { code: '+213',  iso: 'DZ', country: 'Algeria' },
  { code: '+376',  iso: 'AD', country: 'Andorra' },
  { code: '+244',  iso: 'AO', country: 'Angola' },
  { code: '+1268', iso: 'AG', country: 'Antigua and Barbuda' },
  { code: '+54',   iso: 'AR', country: 'Argentina' },
  { code: '+374',  iso: 'AM', country: 'Armenia' },
  { code: '+61',   iso: 'AU', country: 'Australia' },
  { code: '+43',   iso: 'AT', country: 'Austria' },
  { code: '+994',  iso: 'AZ', country: 'Azerbaijan' },
  { code: '+1242', iso: 'BS', country: 'Bahamas' },
  { code: '+973',  iso: 'BH', country: 'Bahrain' },
  { code: '+880',  iso: 'BD', country: 'Bangladesh' },
  { code: '+1246', iso: 'BB', country: 'Barbados' },
  { code: '+375',  iso: 'BY', country: 'Belarus' },
  { code: '+32',   iso: 'BE', country: 'Belgium' },
  { code: '+501',  iso: 'BZ', country: 'Belize' },
  { code: '+229',  iso: 'BJ', country: 'Benin' },
  { code: '+975',  iso: 'BT', country: 'Bhutan' },
  { code: '+591',  iso: 'BO', country: 'Bolivia' },
  { code: '+387',  iso: 'BA', country: 'Bosnia and Herzegovina' },
  { code: '+267',  iso: 'BW', country: 'Botswana' },
  { code: '+55',   iso: 'BR', country: 'Brazil' },
  { code: '+673',  iso: 'BN', country: 'Brunei' },
  { code: '+359',  iso: 'BG', country: 'Bulgaria' },
  { code: '+226',  iso: 'BF', country: 'Burkina Faso' },
  { code: '+257',  iso: 'BI', country: 'Burundi' },
  { code: '+238',  iso: 'CV', country: 'Cabo Verde' },
  { code: '+855',  iso: 'KH', country: 'Cambodia' },
  { code: '+237',  iso: 'CM', country: 'Cameroon' },
  { code: '+1',    iso: 'CA', country: 'Canada' },
  { code: '+236',  iso: 'CF', country: 'Central African Republic' },
  { code: '+235',  iso: 'TD', country: 'Chad' },
  { code: '+56',   iso: 'CL', country: 'Chile' },
  { code: '+86',   iso: 'CN', country: 'China' },
  { code: '+57',   iso: 'CO', country: 'Colombia' },
  { code: '+269',  iso: 'KM', country: 'Comoros' },
  { code: '+242',  iso: 'CG', country: 'Congo' },
  { code: '+243',  iso: 'CD', country: 'Congo (DRC)' },
  { code: '+506',  iso: 'CR', country: 'Costa Rica' },
  { code: '+385',  iso: 'HR', country: 'Croatia' },
  { code: '+53',   iso: 'CU', country: 'Cuba' },
  { code: '+357',  iso: 'CY', country: 'Cyprus' },
  { code: '+420',  iso: 'CZ', country: 'Czech Republic' },
  { code: '+45',   iso: 'DK', country: 'Denmark' },
  { code: '+253',  iso: 'DJ', country: 'Djibouti' },
  { code: '+1767', iso: 'DM', country: 'Dominica' },
  { code: '+1809', iso: 'DO', country: 'Dominican Republic' },
  { code: '+593',  iso: 'EC', country: 'Ecuador' },
  { code: '+20',   iso: 'EG', country: 'Egypt' },
  { code: '+503',  iso: 'SV', country: 'El Salvador' },
  { code: '+240',  iso: 'GQ', country: 'Equatorial Guinea' },
  { code: '+291',  iso: 'ER', country: 'Eritrea' },
  { code: '+372',  iso: 'EE', country: 'Estonia' },
  { code: '+268',  iso: 'SZ', country: 'Eswatini' },
  { code: '+251',  iso: 'ET', country: 'Ethiopia' },
  { code: '+679',  iso: 'FJ', country: 'Fiji' },
  { code: '+358',  iso: 'FI', country: 'Finland' },
  { code: '+33',   iso: 'FR', country: 'France' },
  { code: '+241',  iso: 'GA', country: 'Gabon' },
  { code: '+220',  iso: 'GM', country: 'Gambia' },
  { code: '+995',  iso: 'GE', country: 'Georgia' },
  { code: '+49',   iso: 'DE', country: 'Germany' },
  { code: '+233',  iso: 'GH', country: 'Ghana' },
  { code: '+30',   iso: 'GR', country: 'Greece' },
  { code: '+1473', iso: 'GD', country: 'Grenada' },
  { code: '+502',  iso: 'GT', country: 'Guatemala' },
  { code: '+224',  iso: 'GN', country: 'Guinea' },
  { code: '+245',  iso: 'GW', country: 'Guinea-Bissau' },
  { code: '+592',  iso: 'GY', country: 'Guyana' },
  { code: '+509',  iso: 'HT', country: 'Haiti' },
  { code: '+504',  iso: 'HN', country: 'Honduras' },
  { code: '+852',  iso: 'HK', country: 'Hong Kong' },
  { code: '+36',   iso: 'HU', country: 'Hungary' },
  { code: '+354',  iso: 'IS', country: 'Iceland' },
  { code: '+62',   iso: 'ID', country: 'Indonesia' },
  { code: '+98',   iso: 'IR', country: 'Iran' },
  { code: '+964',  iso: 'IQ', country: 'Iraq' },
  { code: '+353',  iso: 'IE', country: 'Ireland' },
  { code: '+972',  iso: 'IL', country: 'Israel' },
  { code: '+39',   iso: 'IT', country: 'Italy' },
  { code: '+1876', iso: 'JM', country: 'Jamaica' },
  { code: '+81',   iso: 'JP', country: 'Japan' },
  { code: '+962',  iso: 'JO', country: 'Jordan' },
  { code: '+7',    iso: 'KZ', country: 'Kazakhstan' },
  { code: '+254',  iso: 'KE', country: 'Kenya' },
  { code: '+686',  iso: 'KI', country: 'Kiribati' },
  { code: '+850',  iso: 'KP', country: 'Korea (North)' },
  { code: '+82',   iso: 'KR', country: 'Korea (South)' },
  { code: '+383',  iso: 'XK', country: 'Kosovo' },
  { code: '+965',  iso: 'KW', country: 'Kuwait' },
  { code: '+996',  iso: 'KG', country: 'Kyrgyzstan' },
  { code: '+856',  iso: 'LA', country: 'Laos' },
  { code: '+371',  iso: 'LV', country: 'Latvia' },
  { code: '+961',  iso: 'LB', country: 'Lebanon' },
  { code: '+266',  iso: 'LS', country: 'Lesotho' },
  { code: '+231',  iso: 'LR', country: 'Liberia' },
  { code: '+218',  iso: 'LY', country: 'Libya' },
  { code: '+423',  iso: 'LI', country: 'Liechtenstein' },
  { code: '+370',  iso: 'LT', country: 'Lithuania' },
  { code: '+352',  iso: 'LU', country: 'Luxembourg' },
  { code: '+853',  iso: 'MO', country: 'Macau' },
  { code: '+261',  iso: 'MG', country: 'Madagascar' },
  { code: '+265',  iso: 'MW', country: 'Malawi' },
  { code: '+60',   iso: 'MY', country: 'Malaysia' },
  { code: '+960',  iso: 'MV', country: 'Maldives' },
  { code: '+223',  iso: 'ML', country: 'Mali' },
  { code: '+356',  iso: 'MT', country: 'Malta' },
  { code: '+692',  iso: 'MH', country: 'Marshall Islands' },
  { code: '+222',  iso: 'MR', country: 'Mauritania' },
  { code: '+230',  iso: 'MU', country: 'Mauritius' },
  { code: '+52',   iso: 'MX', country: 'Mexico' },
  { code: '+691',  iso: 'FM', country: 'Micronesia' },
  { code: '+373',  iso: 'MD', country: 'Moldova' },
  { code: '+377',  iso: 'MC', country: 'Monaco' },
  { code: '+976',  iso: 'MN', country: 'Mongolia' },
  { code: '+382',  iso: 'ME', country: 'Montenegro' },
  { code: '+212',  iso: 'MA', country: 'Morocco' },
  { code: '+258',  iso: 'MZ', country: 'Mozambique' },
  { code: '+95',   iso: 'MM', country: 'Myanmar' },
  { code: '+264',  iso: 'NA', country: 'Namibia' },
  { code: '+674',  iso: 'NR', country: 'Nauru' },
  { code: '+977',  iso: 'NP', country: 'Nepal' },
  { code: '+31',   iso: 'NL', country: 'Netherlands' },
  { code: '+64',   iso: 'NZ', country: 'New Zealand' },
  { code: '+505',  iso: 'NI', country: 'Nicaragua' },
  { code: '+227',  iso: 'NE', country: 'Niger' },
  { code: '+234',  iso: 'NG', country: 'Nigeria' },
  { code: '+389',  iso: 'MK', country: 'North Macedonia' },
  { code: '+47',   iso: 'NO', country: 'Norway' },
  { code: '+968',  iso: 'OM', country: 'Oman' },
  { code: '+92',   iso: 'PK', country: 'Pakistan' },
  { code: '+680',  iso: 'PW', country: 'Palau' },
  { code: '+970',  iso: 'PS', country: 'Palestine' },
  { code: '+507',  iso: 'PA', country: 'Panama' },
  { code: '+675',  iso: 'PG', country: 'Papua New Guinea' },
  { code: '+595',  iso: 'PY', country: 'Paraguay' },
  { code: '+51',   iso: 'PE', country: 'Peru' },
  { code: '+63',   iso: 'PH', country: 'Philippines' },
  { code: '+48',   iso: 'PL', country: 'Poland' },
  { code: '+351',  iso: 'PT', country: 'Portugal' },
  { code: '+974',  iso: 'QA', country: 'Qatar' },
  { code: '+40',   iso: 'RO', country: 'Romania' },
  { code: '+7',    iso: 'RU', country: 'Russia' },
  { code: '+250',  iso: 'RW', country: 'Rwanda' },
  { code: '+1869', iso: 'KN', country: 'Saint Kitts and Nevis' },
  { code: '+1758', iso: 'LC', country: 'Saint Lucia' },
  { code: '+1784', iso: 'VC', country: 'Saint Vincent and the Grenadines' },
  { code: '+685',  iso: 'WS', country: 'Samoa' },
  { code: '+378',  iso: 'SM', country: 'San Marino' },
  { code: '+239',  iso: 'ST', country: 'Sao Tome and Principe' },
  { code: '+966',  iso: 'SA', country: 'Saudi Arabia' },
  { code: '+221',  iso: 'SN', country: 'Senegal' },
  { code: '+381',  iso: 'RS', country: 'Serbia' },
  { code: '+248',  iso: 'SC', country: 'Seychelles' },
  { code: '+232',  iso: 'SL', country: 'Sierra Leone' },
  { code: '+65',   iso: 'SG', country: 'Singapore' },
  { code: '+421',  iso: 'SK', country: 'Slovakia' },
  { code: '+386',  iso: 'SI', country: 'Slovenia' },
  { code: '+677',  iso: 'SB', country: 'Solomon Islands' },
  { code: '+252',  iso: 'SO', country: 'Somalia' },
  { code: '+27',   iso: 'ZA', country: 'South Africa' },
  { code: '+211',  iso: 'SS', country: 'South Sudan' },
  { code: '+34',   iso: 'ES', country: 'Spain' },
  { code: '+94',   iso: 'LK', country: 'Sri Lanka' },
  { code: '+249',  iso: 'SD', country: 'Sudan' },
  { code: '+597',  iso: 'SR', country: 'Suriname' },
  { code: '+46',   iso: 'SE', country: 'Sweden' },
  { code: '+41',   iso: 'CH', country: 'Switzerland' },
  { code: '+963',  iso: 'SY', country: 'Syria' },
  { code: '+886',  iso: 'TW', country: 'Taiwan' },
  { code: '+992',  iso: 'TJ', country: 'Tajikistan' },
  { code: '+255',  iso: 'TZ', country: 'Tanzania' },
  { code: '+66',   iso: 'TH', country: 'Thailand' },
  { code: '+670',  iso: 'TL', country: 'Timor-Leste' },
  { code: '+228',  iso: 'TG', country: 'Togo' },
  { code: '+676',  iso: 'TO', country: 'Tonga' },
  { code: '+1868', iso: 'TT', country: 'Trinidad and Tobago' },
  { code: '+216',  iso: 'TN', country: 'Tunisia' },
  { code: '+90',   iso: 'TR', country: 'Turkey' },
  { code: '+993',  iso: 'TM', country: 'Turkmenistan' },
  { code: '+688',  iso: 'TV', country: 'Tuvalu' },
  { code: '+256',  iso: 'UG', country: 'Uganda' },
  { code: '+380',  iso: 'UA', country: 'Ukraine' },
  { code: '+971',  iso: 'AE', country: 'UAE' },
  { code: '+44',   iso: 'GB', country: 'United Kingdom' },
  { code: '+1',    iso: 'US', country: 'United States' },
  { code: '+598',  iso: 'UY', country: 'Uruguay' },
  { code: '+998',  iso: 'UZ', country: 'Uzbekistan' },
  { code: '+678',  iso: 'VU', country: 'Vanuatu' },
  { code: '+379',  iso: 'VA', country: 'Vatican City' },
  { code: '+58',   iso: 'VE', country: 'Venezuela' },
  { code: '+84',   iso: 'VN', country: 'Vietnam' },
  { code: '+967',  iso: 'YE', country: 'Yemen' },
  { code: '+260',  iso: 'ZM', country: 'Zambia' },
  { code: '+263',  iso: 'ZW', country: 'Zimbabwe' },
];

/* Custom country-code picker with SVG flags + search */
const CountryCodePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);
  const selected = COUNTRY_CODES.find(c => c.code === value) || COUNTRY_CODES[0];

  const filtered = search.trim()
    ? COUNTRY_CODES.filter(c =>
        c.country.toLowerCase().includes(search.toLowerCase()) ||
        c.code.includes(search)
      )
    : COUNTRY_CODES;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    setOpen(o => !o);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  return (
    <div ref={ref} className="relative" data-testid="signup-country-code">
      <button
        type="button"
        onClick={handleOpen}
        className="h-12 flex items-center gap-1.5 px-2.5 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors min-w-[68px]"
      >
        <ReactCountryFlag countryCode={selected.iso} svg style={{ width: '1.4em', height: '1.4em' }} title={selected.country} />
        <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl w-60 flex flex-col" style={{ maxHeight: '280px' }}>
          <div className="p-2 border-b border-slate-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search country..."
              className="w-full px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-400"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">No results</p>
            )}
            {filtered.map((c, i) => (
              <button
                key={`${c.iso}-${i}`}
                type="button"
                onClick={() => { onChange(c.code); setOpen(false); setSearch(''); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 transition-colors text-left
                  ${c.code === value && c.iso === selected.iso ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
              >
                <ReactCountryFlag countryCode={c.iso} svg style={{ width: '1.3em', height: '1.3em', flexShrink: 0 }} title={c.country} />
                <span className="flex-1 truncate text-xs">{c.country}</span>
                <span className="text-slate-400 text-xs font-mono">{c.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Same INDIAN_STATES list from ClientSignup.js
const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana',
  'Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur',
  'Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman and Nicobar Islands','Chandigarh','Dadra & Nagar Haveli and Daman & Diu',
  'Delhi','Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry',
];
import { PASSWORD_REQUIREMENTS, validatePasswordPolicy } from '@/components/PasswordStrengthChecklist';
const isPasswordStrong = validatePasswordPolicy;

// Normalize API error: Pydantic v2 returns detail as array of objects
const parseApiError = (err, fallback = 'Something went wrong. Please try again.') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || String(e)).join(', ');
  return fallback;
};

export default function ResellerSignup() {
  const [step, setStep] = useState(1); // 1 = account, 2 = business
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    gst_number: '',
    phone: '',
    mobile_country_code: '+91',
    address: '',
    state_name: '',
    role: 'reseller',
    joining_fee: 0
  });
  const [emailCheck, setEmailCheck] = useState({ status: 'idle' }); // idle | checking | available | taken
  const emailDebounceRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Active partner offer (replaces hardcoded Diwali logic)
  const [activeOffer, setActiveOffer] = useState(null);
  const [offerLoaded, setOfferLoaded] = useState(false);

  // CP detection
  const isCP = searchParams.get('type') === 'channel_partner';
  const refCode = (searchParams.get('ref') || '').toUpperCase();
  const [refPartner, setRefPartner] = useState(null); // { partner_name, account_id }
  const [refError, setRefError] = useState(null);

  // Validate CP referral code on mount
  useEffect(() => {
    if (!isCP) return;
    if (!refCode) { setRefError('A valid SP referral link is required to register as a Channel Partner.'); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/validate-referral?code=${refCode}`)
      .then(res => setRefPartner(res.data))
      .catch(() => setRefError('Invalid or expired referral code. Please contact your Synergy Partner.'));
  }, [isCP, refCode]);

  // Fetch active partner offer from admin offer management
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/active-partner-offer`)
      .then(res => {
        const data = res.data;
        // Only treat as active if the offer targets signup page (or all pages)
        const showOnSignup = !data.id || (data.pages || []).some(p => p === 'partner_signup' || p === 'all');
        setActiveOffer(data.id && showOnSignup ? data : null);
      })
      .catch(() => setActiveOffer(null))
      .finally(() => setOfferLoaded(true));
  }, []);

  // Sync joining_fee with active offer's signup_amount
  useEffect(() => {
    if (!offerLoaded || isCP) return;
    if (activeOffer?.signup_is_free) {
      setFormData(f => ({ ...f, joining_fee: 0 }));
    } else if (activeOffer?.signup_amount > 0) {
      setFormData(f => ({ ...f, joining_fee: activeOffer.signup_amount }));
    } else {
      setFormData(f => ({ ...f, joining_fee: 5000 }));
    }
  }, [activeOffer, offerLoaded, isCP]);

  // Debounced duplicate email check
  useEffect(() => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setEmailCheck({ status: 'idle' });
      return;
    }
    setEmailCheck({ status: 'checking' });
    clearTimeout(emailDebounceRef.current);
    emailDebounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/auth/check-email?email=${encodeURIComponent(formData.email)}`);
        setEmailCheck({ status: res.data.available ? 'available' : 'taken' });
      } catch {
        setEmailCheck({ status: 'idle' });
      }
    }, 600);
    return () => clearTimeout(emailDebounceRef.current);
  }, [formData.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData({ ...formData, phone: handleMobileInput(value) });
    } else if (name === 'gst_number') {
      setFormData({ ...formData, gst_number: handleGSTInput(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      toast.error('Please fill all required fields');
      return false;
    }
    if (emailCheck.status === 'taken') {
      toast.error('This email is already registered. Please use a different email.');
      return false;
    }
    if (emailCheck.status === 'checking') {
      toast.error('Please wait while we verify your email...');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (!isPasswordStrong(formData.password)) {
      toast.error('Password does not meet security requirements');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.company_name) {
      toast.error('Please fill company name');
      return false;
    }
    if (!formData.phone || formData.phone.length !== 10) {
      toast.error('Phone Number is required and must be exactly 10 digits');
      return false;
    }
    if (!formData.state_name) {
      toast.error('Please select your State');
      return false;
    }
    if (formData.gst_number && formData.gst_number.length < 15) {
      toast.error('GST Number must be a valid 15-character GSTIN');
      return false;
    }
    return true;
  };

  // CP registration (no payment)
  const completeRegistrationCP = async () => {
    setLoading(true);
    try {
      const { confirmPassword, ...submitData } = formData;
      submitData.role = 'sub_reseller';
      submitData.parent_id = refPartner?.partner_id || null;
      submitData.joining_fee = 0;
      await register(submitData);
      toast.success('Registration successful! Welcome to CloudSetu');
      navigate('/partner/kyc-portal');
    } catch (error) {
      toast.error(parseApiError(error, 'Registration failed. Please contact support.'));
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!validateStep2()) return;

    setLoading(true);

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      const orderResponse = await axios.post(`${API}/payment/create-registration-order`, null, {
        params: {
          amount: formData.joining_fee,
          email: formData.email,
          company_name: formData.company_name,
          phone: formData.phone || ''
        }
      });

      const orderData = orderResponse.data;

      const options = {
        key: orderData.key_id,
        amount: orderData.amount_paise,
        currency: orderData.currency,
        name: 'CloudSetu',
        description: 'Partner Registration Fee',
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.post(`${API}/payment/verify-registration`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              setPaymentCompleted(true);
              setPaymentId(response.razorpay_payment_id);
              toast.success('Payment successful! Completing registration...');
              await completeRegistration(response.razorpay_payment_id);
            }
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        prefill: orderData.prefill,
        theme: { color: '#2563eb' },
        modal: {
          ondismiss: () => setLoading(false)
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setLoading(false);
      });
      razorpay.open();

    } catch (error) {
      toast.error(parseApiError(error, 'Failed to initiate payment'));
      setLoading(false);
    }
  };

  const completeRegistration = async (razorpayPaymentId) => {
    try {
      const { confirmPassword, ...submitData } = formData;
      submitData.razorpay_payment_id = razorpayPaymentId;
      
      await register(submitData);
      toast.success('Registration successful! Welcome to CloudSetu');
      navigate('/reseller/dashboard');
    } catch (error) {
      toast.error(parseApiError(error, 'Registration failed after payment. Please contact support.'));
    } finally {
      setLoading(false);
    }
  };

  // Free registration during Diwali offer (SP only, no payment)
  const completeRegistrationFree = async () => {
    setLoading(true);
    try {
      const { confirmPassword, ...submitData } = formData;
      submitData.joining_fee = 0;
      await register(submitData);
      toast.success('Registration successful! Welcome to CloudSetu — complete KYC to redeem your coupon');
      navigate('/reseller/dashboard');
    } catch (error) {
      toast.error(parseApiError(error, 'Registration failed. Please contact support.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    if (isCP) {
      await completeRegistrationCP();
    } else if (activeOffer?.signup_is_free) {
      await completeRegistrationFree();
    } else {
      await handlePayment();
    }
  };

  const goToStep2 = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  // ── Signup return JSX — replace your existing return(...) with this ──
  // All existing state/logic above remains unchanged.
  // Only the layout wrapper changes: offer left panel + form right panel.

  // Offer bullet points (same pattern as Login)
  const offerBullets = activeOffer?.bullets || [
    activeOffer?.wallet_credit_amount > 0 && `₹${Number(activeOffer.wallet_credit_amount).toLocaleString('en-IN')} wallet credit on signup`,
    'No hidden charges',
    'Instant account activation',
    '24/7 dedicated support',
  ].filter(Boolean);

  // Whether to show split layout:
  // Only SP signup + active offer with signup display_type banner/both
  const showSplitLayout = !isCP && activeOffer &&
    (activeOffer.display_type === 'banner' || activeOffer.display_type === 'both');

   return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: '#fff' }}
    >

      <div
        className="w-full flex rounded-2xl overflow-hidden shadow-[0_8px_48px_rgba(15,30,61,0.18),0_0_0_1px_rgba(255,255,255,0.9)] signup-card-wrap"
        style={{ position: 'relative', zIndex: 1, maxWidth: 900 }}
        data-testid="signup-card"
      >

        {/* ── LEFT PANEL: always show branding + offer details below if active ── */}
        <div
          className="flex flex-col p-8 signup-offer-panel overflow-y-auto"
          style={{
            background: (!isCP && activeOffer?.bg_color) ? activeOffer.bg_color : 'linear-gradient(145deg, #1a3a8f 0%, #0f2260 100%)',
            color: (!isCP && activeOffer?.text_color) ? activeOffer.text_color : '#fff',
            flex: '0 0 40%',
            minWidth: 0,
          }}
        >
          {/* ── DEFAULT BRANDING (always visible) ── */}
          <span
            className="inline-block self-start px-3 py-1 rounded-full text-xs font-bold mb-5"
            style={{ background: '#FF6B35', color: '#fff' }}
          >
            ✦ Partner Program
          </span>

          <p className="font-extrabold leading-snug mb-2" style={{ fontSize: 'clamp(20px, 2.5vw, 26px)' }}>
            Welcome to CloudSetu Partner Portal
          </p>
          <p className="text-sm mb-1" style={{ opacity: 0.75 }}>
            Start your recurring business now.
          </p>
          <p className="text-sm mb-5" style={{ opacity: 0.75 }}>
            Build your white-label hosting business and grow your network.
          </p>

          <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)', // 3 equal columns
                gap: 8,
                marginBottom: 16,
              }}
            >
            {FEATURE_CARDS.map((f, i) => (
              <div
                key={i}
                style={{
                  padding: '5px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: f.color + '33',
                    border: `1px solid ${f.color}66`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    marginBottom: 2,
                  }}
                >
                  {f.icon}
                </span>
                <p style={{ fontSize: 11, fontWeight: 700, opacity: 0.95, lineHeight: 1.2 }}>{f.title}</p>
                <p style={{ fontSize: 10, opacity: 0.6, lineHeight: 1.3 }}>{f.subtitle}</p>
              </div>
            ))}
          </div>

          {/* ── ACTIVE OFFER DETAILS (SP only — hidden for CP) ── */}
          {activeOffer && !isCP && (
            <div
              className="rounded-xl p-4 mt-2"
              style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)' }}
            >
              {/* Offer badge */}
              {activeOffer.badge_text && (
                <span
                  className="inline-block px-2 py-0.5 rounded-full text-xs font-bold mb-3"
                  style={{ background: activeOffer.badge_color || '#FF6B35', color: '#fff' }}
                >
                  {activeOffer.badge_text}
                </span>
              )}

              {/* Wallet credit amount */}
              {activeOffer.wallet_credit_amount > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium mb-0.5" style={{ opacity: 0.75 }}>🎁 You'll receive</p>
                  <p
                    className="font-extrabold leading-none"
                    style={{ fontSize: 'clamp(28px, 4vw, 38px)', color: activeOffer.badge_color || '#FF6B35' }}
                  >
                    ₹{Number(activeOffer.wallet_credit_amount).toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs font-medium mt-0.5" style={{ opacity: 0.75 }}>in Free Wallet Credits</p>
                </div>
              )}

              {/* Offer title if no wallet amount */}
              {!activeOffer.wallet_credit_amount && activeOffer.title && (
                <p className="font-bold mb-2" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>
                  {activeOffer.title}
                </p>
              )}

              {/* Offer bullets */}
              {offerBullets?.length > 0 && (
                <ul className="space-y-1.5 mb-3">
                  {offerBullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ opacity: 0.9 }}>
                      <span
                        className="mt-0.5 flex-shrink-0 rounded-full flex items-center justify-center"
                        style={{ width: 15, height: 15, background: 'rgba(255,255,255,0.2)' }}
                      >
                        <Check size={9} strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}

              {/* Valid till / description */}
              {activeOffer.end_date && (
                <p className="text-xs" style={{ opacity: 0.55 }}>
                  Valid till {activeOffer.end_date.split('T')[0]}
                </p>
              )}
              {!activeOffer.end_date && (
                <p className="text-xs" style={{ opacity: 0.45 }}>
                  {activeOffer.description || 'Limited time offer. T&C apply.'}
                </p>
              )}
            </div>
          )}

          {/* Footer */}
          <p className="text-xs mt-auto pt-6" style={{ opacity: 0.4 }}>
            Trusted by partners across India. No hidden charges.
          </p>
        </div>

        {/* ── RIGHT: Form panel (unchanged) ── */}
        <div className="flex-1 bg-white p-8 min-w-0">

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/cloudsetu-logo.png" alt="CloudSetu" className="h-12 object-contain" />
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-slate-800 mb-2" data-testid="signup-title">
              {isCP ? 'Become a Channel Partner' : 'Become a Partner'}
            </h1>
            <p className="text-slate-500 text-sm">
              {isCP
                ? refPartner ? `Referred by: ${refPartner.partner_name}` : 'Complete your registration below'
                : 'Start your white-label hosting business today'}
            </p>
          </div>

          {/* CP referral error */}
          {isCP && refError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
              <p className="text-sm text-red-700 font-medium">{refError}</p>
              <p className="text-xs text-slate-500 mt-2">Please request a new referral link from your Synergy Partner.</p>
            </div>
          )}

          {/* CP referral loading */}
          {isCP && !refPartner && !refError && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          )}

          {/* Form — same as before, no changes inside */}
          {(!isCP || (isCP && refPartner)) && !refError && (<>

            {/* Step Indicator */}
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>1</div>
                <div className={`w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>2</div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Account Info */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 text-sm font-medium">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="email" name="email" type="email" placeholder="your@email.com"
                        value={formData.email} onChange={handleChange} required
                        className={`h-12 bg-slate-50 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-blue-500 pr-10 ${emailCheck.status === 'taken' ? 'border-red-400' : emailCheck.status === 'available' ? 'border-green-400' : ''}`}
                        data-testid="signup-email-input"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {emailCheck.status === 'checking' && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
                        {emailCheck.status === 'available' && <Check className="h-4 w-4 text-green-500" />}
                        {emailCheck.status === 'taken' && <X className="h-4 w-4 text-red-500" />}
                      </div>
                    </div>
                    {emailCheck.status === 'taken' && (
                      <p className="text-xs text-red-500" data-testid="email-taken-error">This email is already registered. Please use a different email.</p>
                    )}
                    {emailCheck.status === 'available' && (
                      <p className="text-xs text-green-600">Email is available</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700 text-sm font-medium">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                        value={formData.password} onChange={handleChange} required
                        className="h-12 bg-slate-50 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-blue-500 pr-12"
                        data-testid="signup-password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        tabIndex={-1} data-testid="signup-password-toggle">
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {formData.password && (
                      <div className="mt-2 p-3 bg-slate-50 rounded-lg space-y-1" data-testid="password-strength-indicator">
                        {PASSWORD_REQUIREMENTS.map((req, index) => {
                          const passed = req.test(formData.password);
                          return (
                            <div key={index} className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-slate-400'}`}>
                              {passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                              <span>{req.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700 text-sm font-medium">
                      Confirm Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword" name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••"
                        value={formData.confirmPassword} onChange={handleChange} required
                        className={`h-12 bg-slate-50 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-blue-500 pr-12 ${
                          formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-red-300' : ''
                        }`}
                        data-testid="signup-confirm-password-input"
                      />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        tabIndex={-1} data-testid="signup-confirm-password-toggle">
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                    )}
                  </div>

                  <Button type="button" onClick={goToStep2}
                    className="w-full h-12 text-white font-medium rounded-lg mt-4"
                    style={{ background: 'linear-gradient(90deg, #2563eb 0%, #06b6d4 100%)', border: 'none' }}
                    data-testid="next-to-business-button">
                    Next: Business Details
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Step 2: Business Details */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-slate-700 text-sm font-medium">
                      Company Name <span className="text-red-500">*</span>
                    </Label>
                    <Input id="company_name" name="company_name" placeholder="Your Company Pvt Ltd"
                      value={formData.company_name} onChange={handleChange} required
                      className="h-12 bg-slate-50 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-blue-500"
                      data-testid="signup-company-input" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gst_number" className="text-slate-700 text-sm font-medium">
                      GST Number
                    </Label>
                    <Input id="gst_number" name="gst_number" placeholder="22AAAAA0000A1Z5"
                      value={formData.gst_number} onChange={handleChange} maxLength={15}
                      className="h-12 bg-slate-50 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
                      data-testid="signup-gst-input" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 text-sm font-medium">
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <div className="flex gap-1">
                      <CountryCodePicker
                        value={formData.mobile_country_code}
                        onChange={val => setFormData(f => ({ ...f, mobile_country_code: val }))}
                      />
                      <Input id="phone" name="phone" placeholder="10-digit number"
                        value={formData.phone} onChange={handleChange} maxLength={10} required
                        className="h-12 bg-slate-50 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-blue-500 flex-1"
                        data-testid="signup-phone-input" />
                    </div>
                  </div>

                  {/* State — required for correct IGST/CGST+SGST detection */}
                  <div className="space-y-2">
                    <Label htmlFor="state_name" className="text-slate-700 text-sm font-medium">
                      State <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="state_name"
                      name="state_name"
                      value={formData.state_name}
                      onChange={handleChange}
                      required
                      data-testid="signup-state-select"
                      className="w-full h-12 bg-slate-50 border border-slate-200 rounded-lg px-3 text-sm text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">Select your state</option>
                      {INDIAN_STATES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  {/* Payment Info — SP only, hidden when active offer is free */}
                  {!isCP && !activeOffer?.signup_is_free && (
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 mt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-blue-600" />
                            Joining Fee
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {activeOffer?.wallet_credit_amount > 0
                              ? `Get ₹${activeOffer.wallet_credit_amount.toLocaleString('en-IN')} wallet credit after registration`
                              : 'Get ₹11,550 wallet credit after registration'}
                          </p>
                        </div>
                        <p className="text-xl font-bold text-blue-600">
                          ₹{formData.joining_fee.toLocaleString()}
                        </p>
                      </div>
                      {paymentCompleted && (
                        <div className="mt-3 flex items-center gap-2 text-green-600 bg-green-50 rounded-lg p-2">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Payment completed</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Free registration note */}
                  {!isCP && activeOffer?.signup_is_free && (
                    <div className="rounded-xl p-3 mt-4 border" data-testid="offer-free-note"
                      style={{ background: activeOffer.bg_color || '#FFF8F0', borderColor: activeOffer.badge_color || '#FF6B35' }}>
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5" style={{ color: activeOffer.badge_color || '#FF6B35' }}>🎁</span>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: activeOffer.text_color || '#0F1E3D' }}>
                            Free Registration — {activeOffer.name}
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: activeOffer.text_color || '#0F1E3D', opacity: 0.8 }}>
                            No payment required.
                            {activeOffer.wallet_credit_amount > 0 && ` After KYC approval, redeem your coupon from the Wallet tab to get ₹${activeOffer.wallet_credit_amount.toLocaleString('en-IN')} credited.`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 mt-4">
                    <Button type="button" variant="outline" onClick={() => setStep(1)}
                      className="w-full h-12 rounded-lg border-slate-300" data-testid="back-to-account-button">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                    <Button type="submit" className="w-full h-12 text-white font-medium rounded-lg"
                      disabled={loading}
                      style={{ background: 'linear-gradient(90deg, #2563eb 0%, #06b6d4 100%)', border: 'none' }}
                      data-testid="signup-submit-button">
                      {loading ? 'Processing...' : isCP ? 'Register' : (activeOffer?.signup_button_text || 'Pay & Register')}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </>)}

          {/* Footer */}
          <p className="text-sm text-center text-slate-500 mt-6">
            Already have an account?{' '}
            <a href="/partner/login" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="login-link">
              Sign In
            </a>
          </p>

        </div>{/* end form panel */}
      </div>{/* end card wrap */}

      {/* Responsive: stack on mobile */}
      <style>{`
        @media (max-width: 640px) {
          .signup-offer-panel {
            flex: none !important;
            width: 100% !important;
          }
          .signup-card-wrap {
            flex-direction: column !important;
          }
        }
      `}</style>
    </div>
  );


}
